import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CompanyProfileService } from './company-profile.service';

export interface LocaleSettings {
  timezone: string;
  dateMask: string;
  timeFormat: '12' | '24';
  langCode: string;
}

const DEFAULTS: LocaleSettings = {
  timezone: 'UTC',
  dateMask: 'dd.MM.yyyy',
  timeFormat: '24',
  langCode: 'en',
};

/**
 * Company-wide date/time display settings (timezone, date mask, 12/24h
 * format) - loaded once from the company_settings key/value store, same
 * pattern as CurrencyService. AppDatePipe reads this instead of its old
 * hardcoded 'en-GB' / 'dd.MM.yyyy HH:mm'.
 */
@Injectable({
  providedIn: 'root'
})
export class LocaleSettingsService {
  private readonly companyProfile = inject(CompanyProfileService);
  private locale$ = new BehaviorSubject<LocaleSettings>(DEFAULTS);
  private loaded = false;

  loadLocale(): void {
    if (this.loaded) return;

    this.companyProfile.getSettings().subscribe({
      next: (response) => {
        if (response?.success && response.data) {
          const timeFormat = response.data['time_format'] === '12' ? '12' : '24';
          this.locale$.next({
            timezone: response.data['timezone'] || DEFAULTS.timezone,
            dateMask: response.data['date_mask'] || DEFAULTS.dateMask,
            timeFormat,
            langCode: response.data['lang_code'] || DEFAULTS.langCode,
          });
          this.loaded = true;
        }
      },
      error: () => {
        this.loaded = true;
      }
    });
  }

  getLocale(): LocaleSettings {
    return this.locale$.value;
  }

  getLocale$(): Observable<LocaleSettings> {
    return this.locale$.asObservable();
  }

  clearCache(): void {
    this.locale$.next(DEFAULTS);
    this.loaded = false;
  }

  reloadLocale(): void {
    this.loaded = false;
    this.loadLocale();
  }
}
